/**
 * LFD is a calendar day, not an instant.
 *
 * The column is a plain `date`. node-postgres turns it into a JS Date at the
 * *server's* midnight, and JSON.stringify then ships it as an instant:
 *   "2026-09-01T00:00:00.000Z"   server TZ=UTC (prod container)
 *   "2026-09-01T07:00:00.000Z"   server TZ=America/Los_Angeles (dev)
 *   "2026-09-01T23:59:59"        just picked in the date input — no offset
 *   "2026-09-01"                 date-only, straight from a text cast
 *
 * Feeding any of those to `new Date()` and reading it back with getDate()
 * re-interprets the instant in the *viewer's* timezone, and in Los Angeles the
 * first shape lands on Aug 31 — a terminal's 9/1 free day rendered as 8/31,
 * one day of demurrage that isn't there.
 *
 * The leading YYYY-MM-DD is the whole truth in every shape, so read it off the
 * string and rebuild a local-midnight Date. Immune to both the server's
 * timezone and the viewer's.
 */

const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** The calendar day as `YYYY-MM-DD`, or null if there isn't one. */
export function lfdDateOnly(value?: string | null): string | null {
  if (!value) return null;
  const m = CALENDAR_DATE_RE.exec(value);
  return m ? m[0] : null;
}

/** A Date pinned to local midnight of the LFD day — safe for day arithmetic. */
export function parseLfd(value?: string | null): Date | null {
  if (!value) return null;
  const m = CALENDAR_DATE_RE.exec(value);
  if (!m) {
    // Not a shape we recognise. Fall back rather than dropping the value.
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** `09/01` — the compact form used in the Container Monitor table. */
export function formatLfdShort(value?: string | null): string {
  const d = parseLfd(value);
  if (!d) return '-';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

/** `Sep 1` — the dispatch list / detail form. No time: an LFD has no clock. */
export function formatLfdMedium(value?: string | null, options?: { year?: boolean }): string {
  const d = parseLfd(value);
  if (!d) return '-';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(options?.year ? { year: 'numeric' } : {}),
  });
}

/** Whole days from today to the LFD. Negative once it has passed. */
export function daysUntilLfd(value?: string | null): number | null {
  const d = parseLfd(value);
  if (!d) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}
